/**
 * Every style the Contracts screens use, in one place.
 *
 * ⚠️ A `<style>` block in a component is an ORDINARY GLOBAL stylesheet that exists
 * only while that component is mounted — the `ct-` prefix is a naming convention,
 * not a scope. So this is rendered by BOTH entry points (the /contracts page and a
 * job's Contracts tab) and by nothing else, and the table, the chips and the two
 * modals borrow nothing from any other module. `styles/cssCoPresence.spec.ts`
 * checks that on every route.
 *
 * Tokens only (docs: STYLEGUIDE.md): no hex colors, no spacing off the scale.
 * Status colors are always paired with words or a glyph — color is never the only
 * signal.
 */
const css = `
  .ct-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--ew-s-3);
    margin-bottom: var(--ew-s-5);
  }
  .ct-toolbar h1, .ct-toolbar-title {
    font: var(--ew-fw-semibold) var(--ew-fs-h4)/1 var(--ew-font);
    color: var(--ew-dark);
    margin: 0 auto 0 0;
  }
  .ct-toolbar input[type="search"] {
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1 var(--ew-font);
    padding: var(--ew-s-2) var(--ew-s-3);
    border: 1px solid var(--ew-n-300);
    border-radius: var(--ew-r-md);
    background: #fff;
    color: var(--ew-dark);
    flex: 0 1 20rem;
    min-width: 12rem;
  }
  .ct-toolbar label {
    display: inline-flex;
    align-items: center;
    gap: var(--ew-s-2);
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1 var(--ew-font);
    color: var(--ew-n-700);
  }

  .ct-hint {
    background: var(--ew-quality-50);
    color: var(--ew-quality-700);
    border: 1px solid var(--ew-quality-100);
    border-radius: var(--ew-r-md);
    padding: var(--ew-s-2) var(--ew-s-3);
    margin-bottom: var(--ew-s-4);
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/var(--ew-lh-ui) var(--ew-font);
  }

  .ct-kpis {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: var(--ew-s-3);
    margin-bottom: var(--ew-s-5);
  }
  .ct-kpi-label {
    font: var(--ew-fw-medium) var(--ew-fs-caption)/1 var(--ew-font);
    text-transform: uppercase;
    letter-spacing: var(--ew-ls-caption);
    color: var(--ew-n-500);
    margin-bottom: var(--ew-s-2);
  }
  .ct-kpi-value {
    font: var(--ew-fw-semibold) var(--ew-fs-h2)/1 var(--ew-font);
    color: var(--ew-dark);
  }
  .ct-kpi-value--warn { color: var(--ew-safety-700); }

  /* position: relative is load-bearing. .ct-sr is absolutely positioned; with no
     positioned ancestor it is placed against the PAGE, escapes the table's scroll
     clipping, and gives the whole document a horizontal scrollbar on a phone. */
  .ct-section {
    position: relative;
    background: #fff;
    border: 1px solid var(--ew-n-200);
    border-radius: var(--ew-r-lg);
    margin-bottom: var(--ew-s-4);
    overflow: hidden;
  }
  .ct-section-head {
    display: flex;
    align-items: baseline;
    gap: var(--ew-s-3);
    padding: var(--ew-s-3) var(--ew-s-4);
    background: var(--ew-n-50);
    border-bottom: 1px solid var(--ew-n-200);
  }
  .ct-section-title {
    font: var(--ew-fw-semibold) var(--ew-fs-body-ui)/var(--ew-lh-snug) var(--ew-font);
    color: var(--ew-dark);
    margin: 0;
  }
  .ct-section-count {
    font: var(--ew-fw-medium) var(--ew-fs-caption)/1 var(--ew-font);
    color: var(--ew-n-500);
  }
  .ct-section--warn .ct-section-head {
    background: var(--ew-safety-50);
    border-left: var(--ew-s-1) solid var(--ew-safety);
  }
  .ct-section--ok .ct-section-head {
    background: var(--ew-green-50);
    border-left: var(--ew-s-1) solid var(--ew-green);
  }
  .ct-section--muted .ct-section-title { color: var(--ew-n-500); }

  /* Fixed layout with the same columns in every section, so Forms / Job / Vendor
     sit at the same x all the way down the page. min-width keeps a phone
     scrolling INSIDE the section rather than crushing the columns: it is the
     fixed columns added up (49.5rem subcontracts, 59.5rem owner contracts) plus
     room for the ones that flex — vendor and notes, or the contract's name. */
  .ct-table {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/var(--ew-lh-ui) var(--ew-font);
  }
  .ct-table th {
    text-align: left;
    padding: var(--ew-s-2) var(--ew-s-3);
    font: var(--ew-fw-medium) var(--ew-fs-meta)/1 var(--ew-font);
    letter-spacing: var(--ew-ls-caption);
    text-transform: uppercase;
    color: var(--ew-n-500);
    border-bottom: 1px solid var(--ew-n-200);
  }
  .ct-table td {
    padding: var(--ew-s-2) var(--ew-s-3);
    border-bottom: 1px solid var(--ew-n-100);
    vertical-align: top;
    color: var(--ew-dark);
  }
  .ct-table tbody tr:last-child td { border-bottom: 0; }
  .ct-table--subs   { min-width: 71rem; }
  .ct-table--owners { min-width: 76rem; }
  .ct-w-forms  { width: 14rem; }
  .ct-w-job    { width: 8rem; }
  .ct-w-doc    { width: 8rem; }
  .ct-w-date   { width: 7.5rem; }
  .ct-w-person { width: 7rem; }
  .ct-w-status { width: 7.5rem; }
  .ct-w-notes  { width: 10rem; }
  .ct-w-act    { width: 4.5rem; }
  .ct-row--voided td { color: var(--ew-n-400); }
  .ct-row--done td { color: var(--ew-n-500); }
  .ct-strike { text-decoration: line-through; }
  .ct-job a {
    color: var(--ew-dark);
    font-weight: var(--ew-fw-semibold);
    text-decoration: none;
  }
  .ct-job a:hover { text-decoration: underline; }
  .ct-under {
    display: block;
    font: var(--ew-fw-regular) var(--ew-fs-meta)/1.3 var(--ew-font);
    color: var(--ew-n-500);
  }
  .ct-notes { color: var(--ew-n-700); white-space: pre-wrap; }
  .ct-date { white-space: nowrap; }
  .ct-none { color: var(--ew-n-400); }

  .ct-chips { display: inline-flex; flex-wrap: wrap; gap: var(--ew-s-1); }
  .ct-chip {
    display: inline-flex;
    align-items: center;
    gap: var(--ew-s-1);
    padding: var(--ew-s-1) var(--ew-s-2);
    border: 1px solid transparent;
    border-radius: var(--ew-r-sm);
    font: var(--ew-fw-semibold) var(--ew-fs-meta)/1 var(--ew-font);
    white-space: nowrap;
  }
  .ct-chip--received { background: var(--ew-green-50); color: var(--ew-green-700); border-color: var(--ew-green-100); }
  .ct-chip--missing  { background: var(--ew-safety-50); color: var(--ew-safety-700); border-color: var(--ew-safety-100); }
  .ct-chip--waiting  { background: var(--ew-n-50); color: var(--ew-n-500); border-color: var(--ew-n-200); }

  .ct-tag {
    display: inline-block;
    margin-left: var(--ew-s-2);
    padding: 0 var(--ew-s-2);
    border-radius: var(--ew-r-pill);
    background: var(--ew-n-100);
    color: var(--ew-n-700);
    font: var(--ew-fw-semibold) var(--ew-fs-meta)/1.6 var(--ew-font);
    vertical-align: middle;
  }
  .ct-tag--new { background: var(--ew-quality-50); color: var(--ew-quality-700); }

  .ct-empty {
    padding: var(--ew-s-6) var(--ew-s-4);
    text-align: center;
    color: var(--ew-n-500);
    font: var(--ew-fw-regular) var(--ew-fs-body-ui)/var(--ew-lh-ui) var(--ew-font);
  }

  /* ⚠️ minmax(0, 1fr), never a bare 1fr or an implicit column. A bare 1fr is
     minmax(auto, 1fr): the track will not shrink below its widest item, and a
     <select> is as wide as its LONGEST OPTION (a job number plus a long title).
     The owner-contract form lists every job, so one long title pushed the whole
     form past the modal and gave it a sideways scrollbar; a phone did the same to
     the subcontract form. With the tracks pinned to the modal, the dropdown clips
     its own text instead. */
  .ct-form { display: grid; grid-template-columns: minmax(0, 1fr); gap: var(--ew-s-3); }
  .ct-grid2 { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--ew-s-3); }
  @media (max-width: 768px) { .ct-grid2 { grid-template-columns: minmax(0, 1fr); } }
  .ct-field {
    display: flex;
    flex-direction: column;
    gap: var(--ew-s-1);
    font: var(--ew-fw-medium) var(--ew-fs-body-sm)/1.2 var(--ew-font);
    color: var(--ew-n-700);
  }
  .ct-field input, .ct-field select, .ct-field textarea {
    font: var(--ew-fw-regular) var(--ew-fs-body-ui)/var(--ew-lh-ui) var(--ew-font);
    padding: var(--ew-s-2) var(--ew-s-3);
    border: 1px solid var(--ew-n-300);
    border-radius: var(--ew-r-md);
    background: #fff;
    color: var(--ew-dark);
  }
  .ct-field--check { flex-direction: row; align-items: center; gap: var(--ew-s-2); }
  .ct-help {
    font: var(--ew-fw-regular) var(--ew-fs-meta)/1.3 var(--ew-font);
    color: var(--ew-n-500);
  }
  .ct-error {
    background: var(--ew-danger-50);
    color: var(--ew-danger-700);
    border: 1px solid var(--ew-danger-100);
    border-radius: var(--ew-r-md);
    padding: var(--ew-s-2) var(--ew-s-3);
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/var(--ew-lh-ui) var(--ew-font);
  }
  .ct-modal-extra { display: flex; gap: var(--ew-s-2); margin-right: auto; }

  /* Subcontract requests (#627): a full page, not a modal, because the form has about forty fields. Every grid
     is minmax(0, 1fr) for the reason given at .ct-form: a bare 1fr will not shrink below its widest item. */
  .ct-page { max-width: 64rem; }
  .ct-back {
    display: inline-block;
    margin-bottom: var(--ew-s-3);
    color: var(--ew-n-700);
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1 var(--ew-font);
    text-decoration: none;
  }
  .ct-back:hover { text-decoration: underline; }
  .ct-page-head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--ew-s-3);
    margin-bottom: var(--ew-s-4);
  }
  .ct-page-head h1 {
    font: var(--ew-fw-semibold) var(--ew-fs-h4)/var(--ew-lh-snug) var(--ew-font);
    color: var(--ew-dark);
    margin: 0 auto 0 0;
  }
  .ct-rq-section {
    background: #fff;
    border: 1px solid var(--ew-n-200);
    border-radius: var(--ew-r-lg);
    padding: var(--ew-s-4);
    margin-bottom: var(--ew-s-4);
  }
  .ct-rq-section h2 {
    font: var(--ew-fw-semibold) var(--ew-fs-body-ui)/var(--ew-lh-snug) var(--ew-font);
    color: var(--ew-dark);
    margin: 0 0 var(--ew-s-3);
  }
  .ct-grid3 { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: var(--ew-s-3); }
  @media (max-width: 768px) { .ct-grid3 { grid-template-columns: minmax(0, 1fr); } }
  .ct-yn { border: 0; margin: 0; padding: 0; min-width: 0; display: flex; flex-direction: column; gap: var(--ew-s-1); }
  .ct-yn legend {
    padding: 0;
    margin-bottom: var(--ew-s-1);
    font: var(--ew-fw-medium) var(--ew-fs-body-sm)/1.2 var(--ew-font);
    color: var(--ew-n-700);
  }
  .ct-yn-options { display: flex; gap: var(--ew-s-4); }
  .ct-yn-options label {
    display: inline-flex;
    align-items: center;
    gap: var(--ew-s-1);
    font: var(--ew-fw-regular) var(--ew-fs-body-ui)/1 var(--ew-font);
    color: var(--ew-dark);
  }
  .ct-questions { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--ew-s-3) var(--ew-s-4); }
  .ct-question--wide { grid-column: 1 / -1; }
  @media (max-width: 768px) { .ct-questions { grid-template-columns: minmax(0, 1fr); } }
  .ct-actions { display: flex; flex-wrap: wrap; align-items: center; gap: var(--ew-s-2); margin: var(--ew-s-4) 0; }
  .ct-summary {
    margin: 0;
    display: grid;
    grid-template-columns: minmax(0, 14rem) minmax(0, 1fr);
    gap: var(--ew-s-2) var(--ew-s-4);
  }
  @media (max-width: 768px) { .ct-summary { grid-template-columns: minmax(0, 1fr); } }
  .ct-summary-row { display: contents; }
  .ct-summary dt { color: var(--ew-n-500); font: var(--ew-fw-medium) var(--ew-fs-body-sm)/var(--ew-lh-ui) var(--ew-font); }
  .ct-summary dd {
    margin: 0;
    color: var(--ew-dark);
    font: var(--ew-fw-regular) var(--ew-fs-body-ui)/var(--ew-lh-ui) var(--ew-font);
    overflow-wrap: anywhere;
  }
  .ct-summary dd.ct-block { white-space: pre-wrap; }
  .ct-missing {
    background: var(--ew-safety-50);
    color: var(--ew-safety-700);
    border: 1px solid var(--ew-safety-100);
    border-radius: var(--ew-r-md);
    padding: var(--ew-s-2) var(--ew-s-3);
    margin-bottom: var(--ew-s-4);
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/var(--ew-lh-ui) var(--ew-font);
  }
  .ct-missing ul { margin: var(--ew-s-1) 0 0; padding-left: var(--ew-s-5); }
  .ct-page > p.ct-help { margin: 0 0 var(--ew-s-4); }
  .ct-rq-section p.ct-help { margin: 0 0 var(--ew-s-3); }
  .ct-rq-section .ct-number + p.ct-help { margin: var(--ew-s-3) 0 0; }
  .ct-number { display: flex; flex-wrap: wrap; align-items: flex-end; gap: var(--ew-s-2); }
  .ct-number .ct-field { flex: 0 1 16rem; }
  .ct-table--requests { min-width: 62rem; }
  .ct-w-tpl   { width: 9rem; }
  .ct-w-when  { width: 11rem; }
  .ct-w-stand { width: 12rem; }

  /* Documents to include with a request (#656): a drop box and the list under it. */
  .ct-drop {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--ew-s-1);
    width: 100%;
    padding: var(--ew-s-4) var(--ew-s-3);
    border: 2px dashed var(--ew-n-300);
    border-radius: var(--ew-r-md);
    background: #fff;
    color: var(--ew-dark);
    font-family: inherit;
    cursor: pointer;
    transition: background 120ms, border-color 120ms;
  }
  .ct-drop:disabled { cursor: default; opacity: 0.6; }
  .ct-drop--over { border-color: var(--ew-green-500); background: var(--ew-green-50); }
  .ct-drop-title {
    color: var(--ew-green-700);
    font: var(--ew-fw-semibold) var(--ew-fs-body-sm)/var(--ew-lh-ui) var(--ew-font);
    pointer-events: none;
  }
  .ct-drop-sub {
    color: var(--ew-n-500);
    font: var(--ew-fw-regular) var(--ew-fs-meta)/1.3 var(--ew-font);
    pointer-events: none;
  }
  .ct-files { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: var(--ew-s-2); }
  .ct-file {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--ew-s-2);
    padding: var(--ew-s-2) var(--ew-s-3);
    border: 1px solid var(--ew-n-200);
    border-radius: var(--ew-r-md);
    background: #fff;
  }
  .ct-file-main { min-width: 0; display: flex; flex-direction: column; gap: 2px; }
  .ct-file-name {
    background: none;
    border: 0;
    padding: 0;
    text-align: left;
    cursor: pointer;
    color: var(--ew-green-700);
    font: var(--ew-fw-semibold) var(--ew-fs-body-sm)/var(--ew-lh-ui) var(--ew-font);
    overflow-wrap: anywhere;
  }
  .ct-file-name:hover { text-decoration: underline; }
  .ct-file-x {
    flex: 0 0 auto;
    background: none;
    border: 0;
    cursor: pointer;
    color: var(--ew-n-500);
    font-size: 1.1rem;
    line-height: 1;
    padding: 2px var(--ew-s-2);
  }
  .ct-file-x:hover:not(:disabled) { color: var(--ew-danger-700); }
  .ct-file-x:disabled { cursor: default; opacity: 0.5; }

  .ct-sr {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }
`;

export default function ContractsStyles() {
  return <style>{css}</style>;
}
